import React, { useEffect, useState, useMemo } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { IANAZone, Settings } from 'luxon';
import Loading from 'modules/loading/Loading';
import { useMeContext } from 'modules/me';
import { replaceTerms } from './ReplaceTerms';
export const LocaleIntlProvider = ({
  locale,
  ianaName,
  children,
  loadingComponent
}: {
  locale: string;
  ianaName?: string | undefined;
  children?: React.ReactNode;
  loadingComponent?: React.ReactNode;
}): JSX.Element => {
  const [messages, setMessages] = useState<Record<string, string> | null>(null);
  const me = useMeContext();

  useEffect(() => {
    import(`../../locales/${locale}`)
      .then((localeMessages: { default: Record<string, string> }) => {
        const customTerms = me?.locale.customTerms ?? [];
        const localeMsg = replaceTerms(localeMessages.default, customTerms);
        setMessages(localeMsg);
      })
      .catch(async (e) => {
        console.log(`Could not find locale ${locale}, reverting to English`, e);
        const enMessages = await import('locales/en-US');
        setMessages(enMessages.default);
      });
    Settings.defaultLocale = locale;
  }, [locale, me?.locale.customTerms]);

  useEffect(() => {
    if (ianaName && IANAZone.isValidZone(ianaName)) {
      Settings.defaultZone = ianaName;
    }
  }, [ianaName]);

  if (!messages) {
    return loadingComponent ? <>{loadingComponent}</> : <Loading />;
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  );
};

export const useMessage = (
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values?: { [key: string]: any }
): string | undefined => {
  const intl = useIntl();
  return intl.formatMessage({ id }, values);
};

export const useMessages = (
  ids: string[],
  values?: { [key: string]: any }
): Record<string, string> => {
  const intl = useIntl();
  return useMemo(
    () =>
      ids.reduce(
        (obj, id) =>
          Object.assign(obj, { [id]: intl.formatMessage({ id }, values) }),
        {}
      ),
    [ids, intl, values]
  );
};
